import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { Subscription } from 'rxjs';
import { ScreenSize } from 'libs/portal/src/layout/layout.service';
import { CardTableOptions } from 'libs/portal/src/modules/tools/card-table/models';
import { CardTableHeader } from 'libs/portal/src/modules/tools/card-table/models/card-table-header.model';
import { GenericTableHeaderOptions } from 'libs/portal/src/modules/tools/card-table/models/generic-table-header-options.model';
import { GenericService } from 'libs/portal/src/services/generic/generic.service';
import { PaginationService } from 'libs/portal/src/services/pagination/pagination.service';
import { ToolsService } from 'libs/portal/src/services/tools/tools.service';
import { UserRightsService } from 'libs/portal/src/services/user-rights/user-rights.service';
import { Endpoints } from '@huge-monorepo/portal/config';

@Component({
  selector: 'app-relay-port-management',
  templateUrl: './relay-port-management.component.html',
  styleUrls: ['./relay-port-management.component.scss'],
})
export class RelayPortManagementComponent implements OnInit, OnDestroy {
  @ViewChild(MatDatepicker) private rangePicker: MatDatepicker<Date>;

  authSubscription: Subscription;
  obfuscateDetails = false;

  serverLinkPorts: any;
  displayData: any;
  loading = true;
  currentPage = 0;
  today = new Date();
  currentDay = new Date();
  defaultMonthYear = new Date(this.today.getFullYear(), this.today.getMonth());
  dateControl = new UntypedFormControl(new Date());
  ports = [];
  serverNames = [];
  selectedPorts = [];
  selectedServerNames = [];
  resetHeader = false;

  tableHeaders: CardTableHeader[] = [
    {
      ID: 'GroupName',
      Index: 7,
      Sort: 'GroupName',
      Sortable: false,
      Text: 'Group',
      Align: 'left',
      Width: 7,
    },
    {
      ID: 'SeverName',
      Index: 0,
      Sort: 'SeverName',
      Sortable: false,
      Text: 'Server',
      Align: 'left',
      Width: 12,
    },
    {
      ID: 'ServerLinkName',
      Index: 1,
      Sort: 'ServerLinkName',
      Sortable: false,
      Text: 'Server Link',
      Align: 'left',
      Width: 10,
    },
    {
      ID: 'ServerLinkIncomingPort',
      Index: 1,
      Sort: 'ServerLinkIncomingPort',
      Sortable: false,
      Text: 'Incoming Port',
      Align: 'left',
      Width: 7,
    },
    {
      ID: 'OutgoingLinkName',
      Index: 2,
      Sort: 'OutgoingLinkName',
      Sortable: false,
      Text: 'Outgoing Link',
      Align: 'left',
      Width: 10,
    },
    {
      ID: 'OutgoingLinkPort',
      Index: 3,
      Sort: 'OutgoingLinkPort',
      Sortable: false,
      Text: 'Outgoing Port',
      Align: 'left',
      Width: 15,
    },
    {
      ID: 'TotalTransactions',
      Index: 4,
      Sort: 'TotalTransactions',
      Sortable: false,
      Text: 'Total',
      Align: 'left',
      Width: 5,
    },
    {
      ID: 'TotalNullTransactions',
      Index: 5,
      Sort: 'TotalNullTransactions',
      Sortable: false,
      Text: 'Null',
      Align: 'left',
      Width: 7,
    },
    {
      ID: 'AvgConcurrentTransactions',
      Index: 6,
      Sort: 'AvgConcurrentTransactions',
      Sortable: false,
      Text: 'Avg Conn.',
      Align: 'left',
      Width: 7,
    },
    {
      ID: 'TotalSuccessfulTransactions',
      Index: 7,
      Sort: 'TotalSuccessfulTransactions',
      Sortable: false,
      Text: 'Total Success',
      Align: 'left',
      Width: 7,
    },
    {
      ID: 'TotalFailedTransactions',
      Index: 8,
      Sort: 'TotalFailedTransactions',
      Sortable: false,
      Text: 'Total Fail',
      Align: 'left',
      Width: 7,
    },
  ];
  ScreenSize = ScreenSize;
  dataToShow: any[] = [];
  data: any[] = [];
  headerOptions: GenericTableHeaderOptions = {
    ShowSearch: false,
    DefaultDesc: true,
    DateItem: {
      Singular: true,
      Type: 'month',
      Title: 'Select date range',
      StartDate: this.tools.StartOfMonth().toDateString(),
    },
    SortItems: [
      {
        Name: 'Group',
        Display: 'Group',
      },
      {
        Name: 'SeverName',
        Display: 'Server',
      },
      {
        Name: 'ServerLinkName',
        Display: 'Server Link Name',
      },
      {
        Name: 'ServerLinkIncomingPort',
        Display: 'Incoming Link',
      },
      {
        Name: 'OutgoingLinkName',
        Display: 'Outgoing Link Name',
      },
      {
        Name: 'OutgoingLinkPort',
        Display: 'Outgoing Link',
      },
      {
        Name: 'TotalNullTransactions',
        Display: 'Null Transactions',
      },
      {
        Name: 'AvgConcurrentTransactions',
        Display: 'Avg. Conn. Transactions',
      },
      {
        Name: 'TotalSuccessfulTransactions',
        Display: 'Total Success',
      },
      {
        Name: 'TotalFailedTransactions',
        Display: 'Total Fail',
      },
    ],
    DateRequired: true,
    EnableExport: false,
    EnableSelectAll: false,
  };

  options: CardTableOptions = {
    titleProperty: 'Name',
    rowId: 'ID',
    textSize: 'small',
    subTitleProperty: 'data',
    additionalProperty: 'status',
    indicatorColorProperty: 'color',
    selectedProperty: 'selected',
    isSelectable: false,
    flexCardLayout: false,
    propertyRowExclusions: ['ID', 'Description'],
    stylePropertyPrefix: 'alerts',
    clickableFlexCard: false,
  };

  orderBy = '';
  desc = false;

  constructor(
    private genericService: GenericService,
    private endpoints: Endpoints,
    private paginationService: PaginationService,
    private tools: ToolsService,
    private auth: UserRightsService,
  ) {}

  ngOnInit(): void {
    this.dataToShow = [];
    this.loading = true;
    this.authSubscription = this.auth.GetUser().subscribe((user) => {
      const prev = this.obfuscateDetails;
      if (user.isObfuscated) {
        this.obfuscateDetails = true;
      } else {
        this.obfuscateDetails = false;
      }

      if (prev != this.obfuscateDetails) {
        this.loading = true;
        this.dataToShow = [];
        this.serverNames = [];
        this.ports = [];

        this.getOutgoingLinksInfo();
      }
    });
    this.getOutgoingLinksInfo();
  }

  ngOnDestroy(): void {
    this.authSubscription.unsubscribe();
  }

  getOutgoingLinksInfo() {
    this.genericService
      .POST(this.endpoints.GET_RELAY_SERVER_OUTGOING_LINKS(null, 0, '', this.orderBy, this.desc), {
        Month: this.today,
        ServerNames: this.selectedServerNames,
        IncomingPorts: this.selectedPorts,
      })
      .subscribe((results: any) => {
        if (results && results['body']) {
          results['body'].forEach((element) => {
            if (this.obfuscateDetails) {
              element.GroupName = this.tools.Obfuscate(element.GroupName, 'Group ', 4);
              element.SeverName = this.tools.Obfuscate(element.SeverName, 'Server ', 4);
              element.ServerLinkName = this.tools.Obfuscate(element.ServerLinkName, 'ServerLink ', 4);
              element.OutgoingLinkName = this.tools.Obfuscate(element.OutgoingLinkName, 'OutgoingLink ', 4);
            }
            this.dataToShow.push({
              GroupName: element.GroupName == '' ? 'Unassigned' : element.GroupName,
              SeverName: element.SeverName,
              ServerLinkName: element.ServerLinkName,
              ServerLinkIncomingPort: element.ServerLinkIncomingPort,
              OutgoingLinkName: element.OutgoingLinkName,
              OutgoingLinkPort: element.OutgoingLinkPort,
              TotalTransactions: element.TotalTransactions,
              TotalNullTransactions: element.TotalNullTransactions,
              AvgConcurrentTransactions: element.AvgConcurrentTransactions,
              TotalSuccessfulTransactions: element.TotalSuccessfulTransactions,
              TotalFailedTransactions: element.TotalFailedTransactions,
            });
            this.serverNames = [...new Set(results['body'].map((obj) => obj.SeverName))];
            this.ports = [...new Set(results['body'].map((obj) => obj.ServerLinkIncomingPort))];
            this.ports = this.ports.sort();
            this.serverNames = this.serverNames.sort();
            this.headerOptions.DropDownOptions = [
              {
                Label: 'Filter Servername',
                options: this.serverNames,
                attribute: '',
                MultipleDropdowns: true,
              },
              {
                Label: 'Filter Port',
                options: this.ports,
                attribute: '',
                MultipleDropdowns: true,
              },
            ];
          });
        }

        this.loading = false;
      });
  }

  chosenMonthHandler(normalizedMonth: Date) {
    this.loading = true;
    this.dateControl.setValue(normalizedMonth);
    this.today = new Date(this.dateControl.value);
    this.rangePicker.close();
    // this.populateData();
  }

  openPicker(picker: MatDatepicker<Date>) {
    picker.open();
  }

  populateData() {
    this.dataToShow = [];
    this.loading = true;
    this.genericService
      .POST(this.endpoints.GET_RELAY_SERVER_OUTGOING_LINKS(null, 0, '', this.orderBy, this.desc), {
        Month: this.today,
        ServerNames: this.selectedServerNames,
        IncomingPorts: this.selectedPorts,
      })
      .subscribe((results: any) => {
        if (results && results['body']) {
          results['body'].forEach((element) => {
            this.dataToShow.push({
              GroupName: element.GroupName == '' ? 'Unassigned' : element.GroupName,
              SeverName: element.SeverName,
              ServerLinkName: element.ServerLinkName,
              ServerLinkIncomingPort: element.ServerLinkIncomingPort,
              OutgoingLinkName: element.OutgoingLinkName,
              OutgoingLinkPort: element.OutgoingLinkPort,
              TotalTransactions: element.TotalTransactions,
              TotalNullTransactions: element.TotalNullTransactions,
              AvgConcurrentTransactions: element.AvgConcurrentTransactions,
              TotalSuccessfulTransactions: element.TotalSuccessfulTransactions,
              TotalFailedTransactions: element.TotalFailedTransactions,
            });
          });
        }

        this.loading = false;
      });
  }

  onPageChange(event: any) {
    this.currentPage = event;
    const startIndex = (this.currentPage - 1) * 10;
    let endIndex = startIndex + 10;
    if (endIndex > this.data.length) {
      endIndex = this.data.length;
    }
    setTimeout(() => {
      this.paginationService.emit();
    });
  }

  exportToCSV() {
    this.tools.downloadAsCSV(
      this.data,
      'Server Port Details',
      this.tableHeaders.map((item) => {
        return item.Sort;
      }),
    );
  }

  refresh(event: any) {
    this.resetHeader = true;

    this.orderBy = event.Sort ?? '';
    this.desc = event.Desc ?? false;
    this.today = event.FromDate;

    this.populateData();
  }

  dropDownSelect(event: any) {
    if (event.Label == 'Filter Servername') {
      this.selectedServerNames = event.Value;
    } else {
      this.selectedPorts = event.Value;
    }
  }
}
